// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========

import { MorphIcon } from '@/components/ui/morph-icon';
import { exchangeRemoteControlAuth } from '@web/api/remoteControl';
import {
  clearRemoteLoginReturnUrl,
  getRemoteLoginReturnUrl,
  setRemoteAccessToken,
} from '@web/lib/remoteAuth';
import { parseRemoteControlToken } from '@web/lib/remoteControlToken';
import { Loader2 } from 'lucide';
import { useEffect, useState } from 'react';

function callbackParams(): { state: string; rcCode: string } {
  const fragment = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  const query = new URLSearchParams(window.location.search);
  return {
    state: fragment.get('state') || query.get('state') || '',
    rcCode: fragment.get('rc_code') || query.get('rc_code') || '',
  };
}

function remoteSessionIdFromUrl(url: URL): string {
  const match = url.pathname.match(/^\/remote-control\/([^/]+)/);
  if (!match?.[1]) {
    return '';
  }
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

function describeError(err: unknown): string {
  return err instanceof Error
    ? err.message
    : 'Remote control sign-in could not be completed.';
}

export default function RemoteAuthCallbackPage() {
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function completeRemoteAuth() {
      try {
        const { state, rcCode } = callbackParams();
        window.history.replaceState(null, '', '/remote-auth/callback');

        if (!state || !rcCode) {
          throw new Error('Remote control sign-in response is incomplete.');
        }

        const returnUrl = getRemoteLoginReturnUrl(state);
        if (!returnUrl) {
          throw new Error('Remote control return URL was not found.');
        }

        const parsedReturnUrl = new URL(returnUrl);
        const sessionId = remoteSessionIdFromUrl(parsedReturnUrl);
        const linkToken = parseRemoteControlToken(
          parsedReturnUrl.search,
          parsedReturnUrl.hash
        );
        if (!sessionId || !linkToken) {
          throw new Error('Remote control return URL is invalid.');
        }

        const auth = await exchangeRemoteControlAuth(
          sessionId,
          linkToken,
          rcCode
        );
        if (cancelled) return;

        setRemoteAccessToken(sessionId, auth.remote_access_token);
        clearRemoteLoginReturnUrl(state);
        window.location.replace(returnUrl);
      } catch (err) {
        if (!cancelled) {
          setError(describeError(err));
        }
      }
    }

    void completeRemoteAuth();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <main className="text-white flex min-h-screen items-center justify-center bg-zinc-950 px-6">
      <section className="border-white/10 bg-white/[0.06] shadow-black/30 w-full max-w-md rounded-lg border p-6 shadow-2xl">
        <p className="text-xs font-semibold uppercase text-zinc-400">
          Eigent Remote Control
        </p>
        <h1 className="mt-3 text-xl font-semibold">
          {error ? 'Sign-in failed' : 'Completing sign-in'}
        </h1>
        <p className="mt-2 text-sm leading-6 text-zinc-300">
          {error || 'Returning you to the remote control session.'}
        </p>
        {!error && (
          <MorphIcon
            icon={Loader2}
            className="mt-4 h-5 w-5 animate-spin text-zinc-300"
          />
        )}
      </section>
    </main>
  );
}
